import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'src/hooks/use-debounce';
import {
  isPortalSyncNotConfiguredError,
  useAddPortalWorkspacePurchasedCreditsMutation,
  useBlockPortalUserMutation,
  useBypassPortalUserEmailVerificationMutation,
  useGetPortalUsersQuery,
  useSetPortalUserAccessRingMutation,
  useSetPortalUserBillingConversionDisabledMutation,
  useSetPortalUserConversionModalHiddenMutation,
  useSetPortalUserQuotaOverrideMutation,
  useUnblockPortalUserMutation,
  useUpdatePortalSubscriptionTierMutation,
} from '../endpoints/portal-users.endpoints';
import { useUsersTabUrlParams } from './use-users-tab-url-params';
import {
  applyWorkspaceCreditsToUser,
  resolvePurchasedCreditBalance,
} from '../utils/portal-users.mappers';
import type {
  AdminSettableSubscriptionTier,
  PortalUser,
  PortalUserAccessRing,
  PortalUserStatus,
  PortalUserSubscriptionTier,
  AccessRingFilter,
  StatusFilter,
  SubscriptionFilter,
} from '../types/users.types';

const DEFAULT_PAGE_SIZE = 25;

export function useFlowUsers() {
  const {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    subscriptionFilter,
    setSubscriptionFilter,
    accessRingFilter,
    setAccessRingFilter,
    showGuestAccounts,
    setShowGuestAccounts,
    page,
    setPage,
  } = useUsersTabUrlParams();

  const debouncedSearch = useDebounce(search, 500);

  const queryParams = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    subscription: subscriptionFilter === 'all' ? undefined : subscriptionFilter,
    access_ring: accessRingFilter === 'all' ? undefined : accessRingFilter,
    is_guest: showGuestAccounts ? true : undefined,
    page,
    page_size: DEFAULT_PAGE_SIZE,
  }), [debouncedSearch, statusFilter, subscriptionFilter, accessRingFilter, showGuestAccounts, page]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPortalUsersQuery(queryParams);

  const [blockUser] = useBlockPortalUserMutation();
  const [unblockUser] = useUnblockPortalUserMutation();
  const [bypassEmailVerification] = useBypassPortalUserEmailVerificationMutation();
  const [setQuotaOverride] = useSetPortalUserQuotaOverrideMutation();
  const [addPurchasedCredits] = useAddPortalWorkspacePurchasedCreditsMutation();
  const [setBillingConversionDisabled] = useSetPortalUserBillingConversionDisabledMutation();
  const [setConversionModalHidden] = useSetPortalUserConversionModalHiddenMutation();
  const [setAccessRing] = useSetPortalUserAccessRingMutation();
  const [updateSubscriptionTier] = useUpdatePortalSubscriptionTierMutation();

  const [optimisticUsers, setOptimisticUsers] = useState<Record<string, PortalUser>>({});

  const users = useMemo(() => {
    const results = data?.results ?? [];
    return results.map((user) => optimisticUsers[user.id] ?? user);
  }, [data?.results, optimisticUsers]);

  const isPortalSyncNotConfigured = isError && isPortalSyncNotConfiguredError(error);

  const applyBlockAction = useCallback(async(userId: string) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      is_blocked: true,
      status: 'blocked',
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await blockUser(userId).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [blockUser, users]);

  const applyUnblockAction = useCallback(async(userId: string) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      is_blocked: false,
      status: 'active',
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await unblockUser(userId).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [unblockUser, users]);

  const applyEmailVerificationBypass = useCallback(async(userId: string) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      email_verified: true,
      email_verified_at: new Date().toISOString(),
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await bypassEmailVerification(userId).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [bypassEmailVerification, users]);

  const addTokens = useCallback(async(userId: string, amount: number) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return { ok: false as const, message: 'User not found.' };
    }
    if (!previous.workspace_id) {
      return { ok: false as const, message: 'User has no workspace.' };
    }

    const currentPurchased = resolvePurchasedCreditBalance(previous);
    const nextPurchased = currentPurchased + amount;
    const workspaceId = previous.workspace_id;
    const affectedUsers = users.filter((user) => user.workspace_id === workspaceId);
    const optimisticUsersForWorkspace = affectedUsers.map((user) => ({
      ...user,
      purchased_credit_balance: nextPurchased,
      credit_balance: (user.credit_balance ?? currentPurchased) + amount,
    }));

    setOptimisticUsers((current) => {
      const next = { ...current };
      optimisticUsersForWorkspace.forEach((user) => {
        next[user.id] = user;
      });
      return next;
    });

    try {
      const updatedWorkspace = await addPurchasedCredits({
        workspaceId,
        amount,
      }).unwrap();
      setOptimisticUsers((current) => {
        const next = { ...current };
        affectedUsers.forEach((user) => {
          next[user.id] = applyWorkspaceCreditsToUser(
            current[user.id] ?? user,
            updatedWorkspace,
          );
        });
        return next;
      });
      return { ok: true as const };
    } catch (mutationError: unknown) {
      setOptimisticUsers((current) => {
        const next = { ...current };
        affectedUsers.forEach((user) => {
          delete next[user.id];
        });
        return next;
      });
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string' ? responseData.detail : 'Failed to add tokens.';
      return { ok: false as const, message: detail };
    }
  }, [addPurchasedCredits, users]);

  const saveQuotaOverride = useCallback(async(userId: string, value: number | null) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return { ok: false as const, message: 'User not found.' };
    }

    const optimistic: PortalUser = {
      ...previous,
      creative_quota_override: value,
      monthly_limit: value ?? previous.monthly_limit,
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await setQuotaOverride({ userId, creative_quota_override: value }).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
      return { ok: true as const };
    } catch (mutationError: unknown) {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string' ? responseData.detail : 'Failed to save quota override.';
      return { ok: false as const, message: detail };
    }
  }, [setQuotaOverride, users]);

  const setBillingConversionDisabledForUser = useCallback(async(userId: string, value: boolean) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      disable_billing_conversion: value,
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await setBillingConversionDisabled({ userId, disable_billing_conversion: value }).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [setBillingConversionDisabled, users]);

  const setConversionModalHiddenForUser = useCallback(async(userId: string, value: boolean) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      hide_conversion_modal: value,
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await setConversionModalHidden({ userId, hide_conversion_modal: value }).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [setConversionModalHidden, users]);

  const setAccessRingForUser = useCallback(async(
    userId: string,
    accessRing: PortalUserAccessRing
  ) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return { ok: false as const, message: 'User not found.' };
    }

    const optimistic: PortalUser = {
      ...previous,
      access_ring: accessRing,
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await setAccessRing({ userId, access_ring: accessRing }).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
      return { ok: true as const };
    } catch (mutationError: unknown) {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string'
        ? responseData.detail
        : 'Failed to update Early Access.';
      return { ok: false as const, message: detail };
    }
  }, [setAccessRing, users]);

  const saveSubscriptionTier = useCallback(async(
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => {
    const affectedUsers = users.filter((user) => user.workspace_id === workspaceId);
    if (affectedUsers.length === 0) {
      return { ok: false as const, message: 'No users found for this workspace.' };
    }

    const optimisticTier = tier as PortalUserSubscriptionTier;
    setOptimisticUsers((current) => {
      const next = { ...current };
      affectedUsers.forEach((user) => {
        next[user.id] = { ...user, subscription: optimisticTier };
      });
      return next;
    });

    try {
      await updateSubscriptionTier({ workspaceId, tier: optimisticTier }).unwrap();
      return { ok: true as const };
    } catch (mutationError: unknown) {
      setOptimisticUsers((current) => {
        const next = { ...current };
        affectedUsers.forEach((user) => {
          delete next[user.id];
        });
        return next;
      });
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const message = typeof responseData?.message === 'string'
        ? responseData.message
        : typeof responseData?.detail === 'string'
          ? responseData.detail
          : 'Failed to update subscription tier.';
      return { ok: false as const, message };
    }
  }, [updateSubscriptionTier, users]);

  return {
    users,
    totalCount: data?.count ?? 0,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    setPage,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    subscriptionFilter,
    setSubscriptionFilter,
    accessRingFilter,
    setAccessRingFilter,
    showGuestAccounts,
    setShowGuestAccounts,
    isLoading: isLoading || isFetching,
    isError,
    isPortalSyncNotConfigured,
    error,
    refetch,
    applyBlockAction,
    applyUnblockAction,
    applyEmailVerificationBypass,
    addTokens,
    saveQuotaOverride,
    setBillingConversionDisabledForUser,
    setConversionModalHiddenForUser,
    setAccessRingForUser,
    saveSubscriptionTier,
    actionsDisabled: isPortalSyncNotConfigured,
  };
}

export type { PortalUserStatus, SubscriptionFilter, AccessRingFilter };
